/**
 * BrandSocialIcons · v2.6.9 batch BS
 * ====================================
 * Full-color official brand SVGs for Facebook, Instagram, X, TikTok,
 * YouTube — to replace the monochrome Lucide outlines in the marketing
 * footer. Each component returns an inline `<svg>` so the icon scales
 * cleanly at any size and respects the parent's CSS color when needed.
 */

export const FacebookBrandIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
    <path fill="#1877F2" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.014 1.792-4.679 4.533-4.679 1.313 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z"/>
  </svg>
);

export const InstagramBrandIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
    <defs>
      <radialGradient id="ig-grad" cx="0.3" cy="1.05" r="1.2">
        <stop offset="0"   stopColor="#f9ce34"/>
        <stop offset="0.5" stopColor="#ee2a7b"/>
        <stop offset="1"   stopColor="#6228d7"/>
      </radialGradient>
    </defs>
    <rect width="24" height="24" rx="6" fill="url(#ig-grad)"/>
    <path fill="#fff" d="M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Zm0 7.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm5.7-7.7a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"/>
    <path fill="none" stroke="#fff" strokeWidth="1.5" d="M16 5H8a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3Z"/>
  </svg>
);

export const TwitterBrandIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
    <rect width="24" height="24" rx="4" fill="#000000"/>
    <path fill="#fff" d="M18.244 4.5h3.308l-7.227 8.26 8.502 11.24h-6.658l-5.214-6.817L4.99 24H1.68l7.73-8.835L1.255 4.5h6.825l4.713 6.231Zm-1.161 17.52h1.833L7.084 6.375H5.117Z"/>
  </svg>
);

export const TikTokBrandIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
    <rect width="24" height="24" rx="4" fill="#000000"/>
    {/* Cyan offset */}
    <path fill="#25F4EE" d="M16.6 7.3v3a6 6 0 0 1-3.5-1.1v6.3a4.4 4.4 0 1 1-4.4-4.4c.2 0 .5 0 .7.1v3a1.5 1.5 0 1 0 1 1.4V5h2.9a3.6 3.6 0 0 0 3.3 2.3Z"/>
    {/* Magenta offset */}
    <path fill="#FE2C55" d="M17.5 6.5v3a6 6 0 0 1-3.5-1.1v6.3a4.4 4.4 0 1 1-4.4-4.4c.2 0 .5 0 .7.1v3a1.5 1.5 0 1 0 1 1.4V4.2h2.9a3.6 3.6 0 0 0 3.3 2.3Z"/>
    {/* White top */}
    <path fill="#fff" d="M17 7v3a6 6 0 0 1-3.5-1.1v6.3a4.4 4.4 0 1 1-4.4-4.4c.2 0 .5 0 .7.1v3a1.5 1.5 0 1 0 1 1.4V4.7h2.9A3.6 3.6 0 0 0 17 7Z"/>
  </svg>
);

export const YoutubeBrandIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
    <rect x="1" y="4.5" width="22" height="15" rx="4" fill="#FF0000"/>
    <path fill="#fff" d="m10 8.5 6 3.5-6 3.5Z"/>
  </svg>
);

/**
 * Map a CMS key → its branded icon.
 * Returns null for unknown keys.
 */
export function brandIconFor(key) {
  switch ((key || "").toLowerCase()) {
    case "facebook":  return FacebookBrandIcon;
    case "instagram": return InstagramBrandIcon;
    case "twitter":
    case "x":         return TwitterBrandIcon;
    case "tiktok":    return TikTokBrandIcon;
    case "youtube":   return YoutubeBrandIcon;
    default:          return null;
  }
}

/**
 * v2.6.9 batch BS — normalize a user-typed social URL. The CMS was
 * accepting bare strings like "instagram.com/bullertaxi" which the
 * browser then resolved relatively (e.g. bullertaxi.com/instagram.com/…).
 * This helper guarantees an absolute https:// URL.
 */
export function normalizeSocialUrl(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  // Strip a leading "/" that browsers would treat as a relative path.
  return `https://${trimmed.replace(/^\/+/, "")}`;
}
